import type {
  PartnerShipOption,
  PartnerShipOptionCreate,
  PartnerShipOptionEntryCreate,
  PartnerShipOptionEntryUpdate,
  PartnerShipOptionUpdate,
} from '@shipium-corp/shipium-ts-types';
import GatewayAxiosClient from '~/store/services/GatewayAxiosClient';

export const shipOptions = {
  search: {
    queryKey: 'partner-management-service/ship-options',
    configurationType: 'cached' as const,
    fn: async () => {
      const { data } = await GatewayAxiosClient.get<PartnerShipOption[]>(
        '/partner-management-proxy/api/v1/partners/shipOptions?isShipiumShipOption=true',
      );

      return data;
    },
  },
  get: {
    queryKey: 'partner-management-service/ship-options/id',
    configurationType: 'no-cache' as const,
    fn: async (shipOptionId: string) => {
      const { data } = await GatewayAxiosClient.get<PartnerShipOption>(
        `/partner-management-proxy/api/v1/partners/shipOptions/${shipOptionId}`,
      );

      return data;
    },
  },
  create: async (payload: PartnerShipOptionCreate) => {
    const { data } = await GatewayAxiosClient.post<PartnerShipOption>(
      '/partner-management-proxy/api/v1/partners/shipOptions',
      payload,
    );

    return { data, invalidateQueryKeys: [['partner-management-service/ship-options']] };
  },
  createEntry: async ({ shipOptionId, payload }: { shipOptionId: string; payload: PartnerShipOptionEntryCreate }) => {
    const { data } = await GatewayAxiosClient.post<PartnerShipOption>(
      `/partner-management-proxy/api/v1/partners/shipOptions/${shipOptionId}/entry`,
      payload,
    );

    return {
      data,
      invalidateQueryKeys: [['partner-management-service/ship-options']],
      updateQueryData: [
        {
          queryKey: ['partner-management-service/ship-options/id', shipOptionId],
          data,
        },
      ],
    };
  },
  update: async ({
    shipOptionId,
    payload: { partnerProvidedId, ...payload },
  }: {
    shipOptionId: string;
    payload: PartnerShipOptionUpdate;
  }) => {
    console.debug(`Omitting partnerProvidedId for ship option update: ${partnerProvidedId}`);
    const { data } = await GatewayAxiosClient.put<PartnerShipOption>(
      `/partner-management-proxy/api/v1/partners/shipOptions/${shipOptionId}`,
      payload,
    );

    return {
      data,
      invalidateQueryKeys: [['partner-management-service/ship-options']],
      updateQueryData: [
        {
          queryKey: ['partner-management-service/ship-options/id', shipOptionId],
          data,
        },
      ],
    };
  },
  updateEntry: async ({
    shipOptionId,
    shipOptionEntryId,
    payload,
  }: {
    shipOptionId: string;
    shipOptionEntryId: string;
    payload: PartnerShipOptionEntryUpdate;
  }) => {
    const { data } = await GatewayAxiosClient.put<PartnerShipOption>(
      `/partner-management-proxy/api/v1/partners/shipOptions/${shipOptionId}/entry/${shipOptionEntryId}`,
      payload,
    );

    return {
      data,
      invalidateQueryKeys: [['partner-management-service/ship-options']],
      updateQueryData: [
        {
          queryKey: ['partner-management-service/ship-options/id', shipOptionId],
          data,
        },
      ],
    };
  },
  deleteEntry: async ({ shipOptionId, shipOptionEntryId }: { shipOptionId: string; shipOptionEntryId: string }) => {
    const { data } = await GatewayAxiosClient.delete<PartnerShipOption>(
      `/partner-management-proxy/api/v1/partners/shipOptions/${shipOptionId}/entry/${shipOptionEntryId}`,
    );

    return {
      data,
      invalidateQueryKeys: [['partner-management-service/ship-options']],
      updateQueryData: [
        {
          queryKey: ['partner-management-service/ship-options/id', shipOptionId],
          data,
        },
      ],
    };
  },
};
